import React, { useLayoutEffect } from "react";
import classNames from "classnames";
import {
  Outlet,
  ScrollRestoration,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { InformationCircleIcon } from "@heroicons/react/24/solid";

import AppBar from "@components/layout/zenith/AppBar";
import AppSidebar from "@components/layout/zenith/AppSidebar";
import AppInformationPanel from "@components/layout/zenith/AppInformationPanel";
import { useAppContext } from "@context/AppContext";
import encryptedStorage from "@services/encryptedStorage";
import { useRefreshToken } from "@hooks/query/useRefreshToken";
import { useSelectedScheme } from "@hooks/useSelectedScheme";
import Loading from "@components/shared/Loading";

const AppLayout: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const {
    isExpandSidebar,
    isExpandInfoPanel,
    setIsExpandInfoPanel,
    setLoggedInInfo,
    infoPanel,
    setInfoPanel,
  } = useAppContext();
  const { pathname } = useLocation();
  const hasInfoPanel = Boolean(infoPanel);

  // Pages set their text in a passive useEffect, which React runs after every
  // layout effect, so this clears the previous page's text without wiping the
  // new page's.
  useLayoutEffect(() => {
    setInfoPanel(undefined);
  }, [pathname, setInfoPanel]);

  const scheme = useSelectedScheme();
  const { isLoading: isRefreshTokenLoading } = useRefreshToken({
    onSuccess: (data: any) => {
      encryptedStorage.setItem("token", data.token);
      encryptedStorage.setItem("isLoggedIn", "true");
      encryptedStorage.setItem("userDetails", JSON.stringify(data.user));

      setLoggedInInfo({
        userDetails: data.user,
        token: data.token,
        isLoggedIn: true,
      });
    },
    onError: (error: any) => {
      const { status } = error.response;
      const isUnAuthenticateError = status > 400 && status < 500;
      if (isUnAuthenticateError) {
        navigate("/zenith/login");
      }
    },
  });

  if (isRefreshTokenLoading) {
    return (
      <div className="h-screen flex justify-center items-center">
        <Loading />
      </div>
    );
  }

  // A switch remounts everything below the session: no query observer can
  // then carry one scheme's answer (keepPreviousData) into another, and the
  // page's selections, filters and page numbers belonged to the old scheme.
  return (
    <div key={scheme ?? "home"} className="min-h-screen bg-gray-50">
      <AppBar />
      <AppSidebar />
      {hasInfoPanel ? (
        <AppInformationPanel>{infoPanel}</AppInformationPanel>
      ) : null}

      <div
        className={classNames(
          "flex min-h-screen flex-col pt-14 transition-all duration-200",
          isExpandSidebar ? "lg:pl-56" : "lg:pl-14",
          { "xl:pr-96": hasInfoPanel && isExpandInfoPanel },
        )}
      >
        <main className="flex flex-1 flex-col p-4 sm:p-6">
          <Outlet />
        </main>

        <button
          type="button"
          title="Show information panel"
          className={classNames(
            "hidden fixed bottom-14 right-8 z-10 text-primary-1 opacity-70 hover:opacity-100",
            {
              "xl:block": hasInfoPanel && !isExpandInfoPanel,
            },
          )}
          onClick={() => {
            setIsExpandInfoPanel(true);
          }}
        >
          <InformationCircleIcon className="w-8 h-8" />
        </button>
      </div>

      <ScrollRestoration />
    </div>
  );
};

export default AppLayout;
